import { useState, type ReactElement } from 'react'
import { isEmoji } from '@front/model'
import { Group, Input, Radio, Stack, TextInput } from '@mantine/core'
import { COMPANY_ICON_ID, isCustomIconId } from '@terros/common'
import { MaterialSymbolSelector } from './MaterialSymbolSelector'
import { type TerrosIconType } from './types'

type Props = {
  value?: string
  placeholder?: string
  onChange: (icon: string) => void
  withinPortal?: boolean
  disableCompanyLogo?: boolean
}

export const IconEmojiPicker = ({
  value,
  onChange,
  placeholder,
  withinPortal,
  disableCompanyLogo,
}: Props): ReactElement => {
  const [iconType, setIconType] = useState<TerrosIconType>(getIconType(value, Boolean(disableCompanyLogo)))
  const [lastIcon, setLastIcon] = useState<string | undefined>(
    value && (isEmoji(value) || isCustomIconId(value)) ? undefined : value
  )
  const [lastEmoji, setLastEmoji] = useState<string | undefined>(value && isEmoji(value) ? value : undefined)

  const onIconValueChange = (value: string): void => {
    if (iconType === 'emoji') {
      setLastEmoji(value)
    } else {
      setLastIcon(value)
    }
    onChange(value)
  }

  const onIconTypeChange = (value: string): void => {
    if (value === 'custom') {
      setIconType(value)
      onChange(COMPANY_ICON_ID)
      return
    }
    if (value === 'emoji') {
      onChange(lastEmoji ?? '')
      setIconType(value)
    } else if (value === 'material') {
      onChange(lastIcon ?? '')
      setIconType(value)
    }
  }
  return (
    <Stack>
      <Radio.Group
        description='You can input an emoji or choose an icon'
        label='Icon Type'
        name='iconType'
        value={iconType}
        onChange={onIconTypeChange}
      >
        <Group mt='xs'>
          <Radio label='Use native emoji' value='emoji' />
          <Radio label='Use icon' value='material' />
          {!disableCompanyLogo && <Radio label='Use company logo' value='custom' />}
        </Group>
      </Radio.Group>
      {iconType !== 'custom' && (
        <IconEmojiInput
          iconType={iconType}
          onChange={onIconValueChange}
          placeholder={placeholder}
          value={value}
          withinPortal={withinPortal}
        />
      )}
    </Stack>
  )
}

type InputProps = Pick<Props, 'value' | 'onChange' | 'placeholder'> & {
  iconType: TerrosIconType
  withinPortal?: boolean
}
const IconEmojiInput = ({ iconType, withinPortal, ...props }: InputProps): ReactElement => {
  const activeValue = getActiveValue(props.value, iconType)
  return (
    <>
      {iconType === 'emoji' && <EmojiField {...props} value={activeValue} />}
      {iconType === 'material' && <MaterialSymbolSelector {...props} value={activeValue} withinPortal={withinPortal} />}
    </>
  )
}

type FieldProps = Pick<Props, 'value' | 'onChange' | 'placeholder'>
const EmojiField = ({ value, onChange, placeholder }: FieldProps): ReactElement => {
  return (
    <>
      <Input.Description>
        Enter an emoji to use as the icon. To show the emoji chooser, press ⌃⌘␣ (macOS) or Win + . (Windows)
      </Input.Description>
      <TextInput
        onChange={(event) => {
          onChange(event.currentTarget.value)
        }}
        placeholder={placeholder}
        value={value}
      />
    </>
  )
}

const getActiveValue = (icon: string | undefined, iconType: TerrosIconType): string | undefined => {
  if (!icon) return
  if (iconType === 'emoji' && isEmoji(icon)) return icon
  if (iconType === 'material' && !isEmoji(icon)) return icon
}

const getIconType = (icon: string | undefined, disableCompanyLogo: boolean): TerrosIconType => {
  if (!icon) return 'material'
  if (isEmoji(icon)) return 'emoji'
  if (disableCompanyLogo) return 'material'
  if (isCustomIconId(icon)) return 'custom'
  return 'material'
}
